import { FunnelResponse, LeadResponse } from '@troia-v3/types';
import type { KanbanBoardAggregates, KanbanColumnPage, LeadKanbanCard } from '@troia-v3/types';
import { KanbanColumnType } from '@/shared/components/kanban';

/**
 * Possible scopes for leads access control
 */
export type LeadsScope = 'own' | 'team' | 'all';

/**
 * Actual permission structure returned by the API
 * This differs from UserPermissions in types package which uses string arrays
 *
 * @deprecated Mantido só pra compatibilidade. O scope efetivo de leads não vem
 * mais de `permissions.crm.scope` (módulo CRM perdeu scope) — vem do
 * `funnels-users.scope` per-funil. Use `funnel.userScope` ou os helpers
 * `getEffectiveScopeForFunnels` / `mapScopeToDropdownFlag` deste arquivo.
 */
export interface ModulePermission {
  actions: string[];
  scope: LeadsScope;
}

export interface ActualUserPermissions {
  crm?: ModulePermission;
  [key: string]: ModulePermission | undefined;
}

/**
 * @deprecated Use `getEffectiveScopeForFunnels(funnels)` em vez. O scope
 * efetivo veio pra `funnels-users.scope` (per-funil) — D-context-gate
 * (CLAUDE.md raiz NUNCA #47, 2026-04-24). Permissão de módulo CRM não tem
 * mais campo `scope`. Mantido temporariamente pra compat com fluxos antigos.
 */
export function getUserLeadsScope(permissions: ActualUserPermissions | undefined): LeadsScope {
  if (!permissions) return 'own';
  if (permissions.crm?.scope) {
    const scope = permissions.crm.scope;
    if (scope === 'all' || scope === 'team' || scope === 'own') return scope;
  }
  return 'own';
}

// ============================================================================
// NEW SCOPE LOGIC (post D-context-gate, 2026-04-24)
// ============================================================================

/**
 * Resolve o scope efetivo do user logado a partir de uma lista de funis (com
 * `userScope` hidratado pelo backend a partir de `funnels-users`).
 *
 * Regra (alinhada com backend `buildEntityAccessFilter`):
 * - Se algum funil tem scope `'all'` → retorna `'all'`
 * - Senão se algum funil tem scope `'team'` → retorna `'team'`
 * - Senão (todos `'own'` ou null) → retorna `'own'`
 *
 * Usado em listagens cross-funnel (`LeadsListPage`) onde o user pode ter scope
 * diferente em cada funil. Em pages per-funil (kanban), use `funnel.userScope`
 * direto.
 */
export function getEffectiveScopeForFunnels(funnels: Pick<FunnelResponse, 'userScope'>[]): LeadsScope {
  if (funnels.some((f) => f.userScope === 'all')) return 'all';
  if (funnels.some((f) => f.userScope === 'team')) return 'team';
  return 'own';
}

/**
 * Mapeia scope efetivo para a flag passada ao `useUsersDropdown`:
 * - `'all'` → `'all'` (mostra todos os users do tenant)
 * - `'team'` → `'team'` (backend filtra via team-users)
 * - `'own'` → `undefined` (não exibir filtro; flag não importa)
 */
export function mapScopeToDropdownFlag(scope: LeadsScope): 'team' | 'all' | undefined {
  if (scope === 'all') return 'all';
  if (scope === 'team') return 'team';
  return undefined;
}

/**
 * Convert Leads to Kanban Columns based on Funnel Steps
 *
 * Organizes leads into lanes representing stages of the sales funnel:
 * - Each column represents a funnel step
 * - Leads are grouped by their current step (stepId field)
 * - Returns empty array if no funnel steps are provided
 *
 * @param leads - Array of leads to organize
 * @param funnelSteps - Funnel steps to use as columns (required)
 * @returns Array of Kanban columns with organized leads, or empty array if no steps
 */
export function convertLeadsToKanbanColumns(
  leads: LeadResponse[],
  funnelSteps?: Array<{ id: string; name: string; color: string; order: number }>
): KanbanColumnType<LeadResponse>[] {

  // Se não houver etapas definidas, retornar array vazio
  if (!funnelSteps || funnelSteps.length === 0) {
    return [];
  }

  // Sort steps by order
  const sortedSteps = [...funnelSteps].sort((a, b) => a.order - b.order);

  // Create columns for each step
  return sortedSteps.map((step) => {
    // Filter leads that belong to this step
    const stepLeads = leads.filter(lead => lead.stepId === step.id);

    // Calculate total value (sum of budgets) for this step
    const totalValue = stepLeads.reduce((sum, lead) => sum + (lead.budget || 0), 0);

    return {
      id: step.id,
      title: step.name,
      color: step.color,
      items: stepLeads,
      count: stepLeads.length,
      totalValue
    };
  });
}

/**
 * Calculate Funnel Metrics
 *
 * Provides aggregated metrics across the sales funnel
 *
 * @param leads - Array of leads
 * @param funnelSteps - Optional custom funnel steps
 * @returns Metrics object with counts and conversion rates
 */
export function calculateFunnelMetrics(
  leads: LeadResponse[],
  funnelSteps?: Array<{ id: string; name: string; color: string; order: number }>
): {
  total: number;
  totalValue: number;
  conversionRate: number;
  stepCounts: Record<string, number>;
  valueByStep: Array<{ stepId: string; stepName: string; value: number }>;
  priorityCounts: {
    urgent: number;
    high: number;
    medium: number;
    low: number;
  };
  businessStatusCounts: {
    pending: number;
    won: number;
    lost: number;
  };
  activeLeads: number;
  wonLeads: number;
  lostLeads: number;
} {
  const columns = convertLeadsToKanbanColumns(leads, funnelSteps);

  // Calculate total estimated value (using budget field)
  const totalValue = leads.reduce((sum, lead) => {
    return sum + (lead.budget || 0);
  }, 0);

  // Calculate value by step
  const valueByStep = columns.map(column => ({
    stepId: column.id,
    stepName: column.title,
    value: column.items.reduce((sum, lead) => sum + (lead.budget || 0), 0)
  }));

  // Count by priority
  const priorityCounts = {
    urgent: leads.filter(l => l.priority === 'urgent').length,
    high: leads.filter(l => l.priority === 'high').length,
    medium: leads.filter(l => l.priority === 'medium').length,
    low: leads.filter(l => l.priority === 'low').length
  };

  // Count by business status
  const businessStatusCounts = {
    pending: leads.filter(l => l.businessStatus === 'pending').length,
    won: leads.filter(l => l.businessStatus === 'won').length,
    lost: leads.filter(l => l.businessStatus === 'lost').length
  };

  // Calculate conversion rate (won / total * 100)
  const conversionRate = leads.length > 0
    ? (businessStatusCounts.won / leads.length) * 100
    : 0;

  // Count by step
  const stepCounts = columns.reduce((acc, column) => {
    acc[column.id] = column.count ?? column.items.length;
    return acc;
  }, {} as Record<string, number>);

  return {
    total: leads.length,
    totalValue,
    conversionRate: Math.round(conversionRate * 10) / 10, // Round to 1 decimal
    stepCounts,
    valueByStep,
    priorityCounts,
    businessStatusCounts,
    activeLeads: businessStatusCounts.pending,
    wonLeads: businessStatusCounts.won,
    lostLeads: businessStatusCounts.lost
  };
}

/**
 * Métricas do funil a partir dos agregados do backend (modo kanban) —
 * mesmo shape do calculateFunnelMetrics (que permanece para table/dashboard).
 */
export function metricsFromAggregates(
  aggregates: KanbanBoardAggregates,
  columns: Array<Pick<KanbanColumnPage<LeadKanbanCard>, 'columnId' | 'count' | 'totalValue'>>,
  stepNames: Map<string, string>,
): ReturnType<typeof calculateFunnelMetrics> {
  const conversionRate = aggregates.total > 0
    ? Math.round((aggregates.businessStatusCounts.won / aggregates.total) * 1000) / 10
    : 0;
  return {
    total: aggregates.total,
    totalValue: aggregates.totalValue,
    conversionRate,
    stepCounts: Object.fromEntries(columns.map((c) => [c.columnId, c.count])),
    valueByStep: columns.map((c) => ({
      stepId: c.columnId,
      stepName: stepNames.get(c.columnId) ?? '',
      value: c.totalValue,
    })),
    priorityCounts: aggregates.priorityCounts,
    businessStatusCounts: aggregates.businessStatusCounts,
    activeLeads: aggregates.businessStatusCounts.pending,
    wonLeads: aggregates.businessStatusCounts.won,
    lostLeads: aggregates.businessStatusCounts.lost,
  };
}

/**
 * Filter leads by funnel ID
 *
 * @param leads - Array of leads
 * @param funnelId - Funnel ID to filter by (null = all funnels)
 * @returns Filtered leads
 */
export function filterLeadsByFunnel<T extends LeadResponse>(
  leads: T[],
  funnelId: string | null
): T[] {
  if (funnelId === null) {
    return leads;
  }

  return leads.filter(lead => lead.funnelId === funnelId);
}

/**
 * Sort leads by priority (urgent → high → medium → low)
 *
 * @param leads - Array of leads
 * @returns Sorted leads
 */
export function sortLeadsByPriority(leads: LeadResponse[]): LeadResponse[] {
  const priorityOrder = {
    urgent: 1,
    high: 2,
    medium: 3,
    low: 4
  };

  return [...leads].sort((a, b) => {
    const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] || 999;
    const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] || 999;
    return aPriority - bPriority;
  });
}

/**
 * Get leads that need follow-up (overdue activities)
 *
 * @param leads - Array of leads
 * @returns Leads with overdue activities
 */
export function getLeadsNeedingFollowUp(leads: LeadResponse[]): LeadResponse[] {
  return leads.filter(lead => lead.activityStatus === 'overdue');
}
